import { graphql } from '@/__generated__';
import {
  AppliedResourcesFilter,
  AppliedResourcesQuery,
  ResourceNodeType,
} from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';
import { toAppliedLineageNode } from '../dbtStrategy/useAppliedLineage';
import { useLazyQuery } from '../useLazyQuery';

/**
 * TODO: Does not yet support `Analysis` and `Metric`
 * Nate says that Analysis shouldn't be in the DAG!
 */
export const GetAppliedResources = graphql(/* GraphQL */ `
  query AppliedResources(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $filter: AppliedResourcesFilter!
  ) {
    environment(id: $environmentId) {
      applied {
        lastUpdatedAt
        resources(filter: $filter, first: $first, after: $after) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              projectId
              environmentId
              ... on ExposureAppliedStateNode {
                parents {
                  uniqueId
                }
              }
              ... on ModelAppliedStateNode {
                parents {
                  uniqueId
                }
                access
                group
              }
              ... on SnapshotAppliedStateNode {
                parents {
                  uniqueId
                }
              }
              ... on TestAppliedStateNode {
                parents {
                  uniqueId
                }
              }
            }
          }
        }
      }
    }
  }
`);

type AppliedResourcesByEnvironmentParams = {
  /** Max of {@link MAX_PAGE_SIZE} */
  first: number;
  after?: string;
  environmentId: number | undefined;
  publicParents?: string[] | undefined;
  pause?: boolean;
  filter?: AppliedResourcesFilter;
};

const DEFAULT_FILTER: AppliedResourcesFilter = {
  types: [
    ResourceNodeType.Exposure,
    ResourceNodeType.Macro,
    ResourceNodeType.Model,
    ResourceNodeType.Seed,
    ResourceNodeType.Snapshot,
    ResourceNodeType.Source,
    ResourceNodeType.Test,
  ],
  publicParents: null,
  tags: null,
  uniqueIds: null,
};

export const useDiscoveryAppliedResources = ({
  environmentId = 0,
  first,
  after,
  pause,
  filter = DEFAULT_FILTER,
}: AppliedResourcesByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetAppliedResources,
      variables: { first, after, environmentId, filter },
      pause: pause || !environmentId,
    },
    (data) => {
      const resources = data.environment?.applied?.resources;
      const pageInfo = resources?.pageInfo;
      if (resources == null || pageInfo == null) return undefined;
      return {
        totalCount: resources.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment?.applied?.resources.edges
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment?.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};

// TODO: maybe we don't need a dedicated hook for this, reuse useDiscoveryAppliedResources
export const useDiscoveryAppliedResourcesWithPublicParents = ({
  environmentId = 0,
  first,
  after,
  pause,
  publicParents,
}: AppliedResourcesByEnvironmentParams) => {
  const { getProjectName } = useDbtCloudAccount();
  const { projectId } = useDbtCloudProject();
  const filter = {
    ...DEFAULT_FILTER,
    publicParents,
  };

  const query = usePaginatedQuery(
    {
      query: GetAppliedResources,
      variables: { first, after, environmentId, filter },
      pause: pause || !environmentId,
    },
    getPageInfo,
    (data) => getResults(data, projectId, getProjectName),
    (data) => data.environment?.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};

const getPageInfo = (data: AppliedResourcesQuery) => {
  const resources = data.environment?.applied?.resources;
  const pageInfo = resources?.pageInfo;
  if (resources == null || pageInfo == null) return undefined;
  return {
    totalCount: resources.totalCount ?? null,
    endCursor: pageInfo.endCursor ?? null,
    hasNextPage: pageInfo.hasNextPage,
  };
};

const getResults = (
  data: AppliedResourcesQuery,
  projectId: number,
  getProjectName: (projectId: number) => string | undefined,
) => {
  return (
    data.environment?.applied?.resources.edges
      .map((edge) => edge.node)
      .filter(truthy)
      .map((node) => toAppliedLineageNode(node as any, projectId, getProjectName)) || []
  );
};

export const useLazyDiscoveryAppliedResourcesWithPublicParents = () => {
  const [query, queryExecute] = useLazyQuery(GetAppliedResources);
  const { getProjectName } = useDbtCloudAccount();
  const { projectId } = useDbtCloudProject();

  const executeQuery = async ({
    environmentId = 0,
    first,
    after,
    publicParents,
  }: AppliedResourcesByEnvironmentParams) => {
    const res = await queryExecute({
      first,
      after: after ?? '',
      environmentId,
      filter: {
        ...DEFAULT_FILTER,
        publicParents: publicParents ?? null,
      },
    });
    return {
      ...res,
      data: res.data ? getResults(res.data, projectId, getProjectName) : [],
      pageInfo: res.data ? getPageInfo(res.data) : undefined,
      fetching: false,
    };
  };

  return [
    {
      ...query,
      data: query.data ? getResults(query.data, projectId, getProjectName) : [],
      pageInfo: query.data ? getPageInfo(query.data) : undefined,
    },
    executeQuery,
  ] as const;
};
